import { createContext, useState, useEffect, useMemo } from "react";
import * as ProductHelper from "helpers/ProductHelper";
import machineService from "services/MachineService";
import { PRODUCT_TYPE, EVENTS } from "const";
import { eventsEmitter } from "utils/events";
import toast from "react-hot-toast";

const ProductContext = createContext();

export function ProductProvider(props) {
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [priceCategories, setPriceCategories] = useState([]);
  const [priceCategoryItems, setPriceCategoryItems] = useState([]);
  const [addonMappings, setAddonMappings] = useState([]);
  const [chargeGroups, setChargeGroups] = useState([]);
  const [charges, setCharges] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchProducts = async (syncFromBE = false, localRefresh = false) => {
    setIsLoading(true);
    try {
      const { categories, items, priceCategories, priceCategoryItems, addonMappings, chargeGroups, charges } =
        await ProductHelper.loadInitialProducts(syncFromBE, localRefresh);

      const sortedCategories = [...categories].sort((a, b) => {
        const orderA = a.order ?? Infinity;
        const orderB = b.order ?? Infinity;
        return orderA - orderB;
      });
      setCategories(sortedCategories);
      setItems(items);
      setPriceCategories(priceCategories || []);
      setPriceCategoryItems(priceCategoryItems || []);
      setAddonMappings(addonMappings || []);
      setChargeGroups(chargeGroups || []);
      setCharges(charges || []);

      if (syncFromBE) {
        machineService.updateProductsToOnlineMachines();
      }
    } catch (error) {
      toast.error("Product load failed");
    } finally {
      setIsLoading(false);
    }
  };

  const updateProduct = async (updatedItem) => {
    setItems((prev) => prev.map((i) => (i.id === updatedItem.id ? updatedItem : i)));

    try {
      await ProductHelper.updateLocalItem(updatedItem);
      await machineService.updateProductsToOnlineMachines(updatedItem);
    } catch (e) {
      console.error("DB Sync failed", e);
    }
  };

  const activeCategoryIds = useMemo(() => {
    return new Set(categories.filter((cat) => ProductHelper.isAvailable(cat)).map((cat) => cat.id));
  }, [categories]);

  const availableItems = useMemo(() => {
    return items
      .filter((item) => ProductHelper.isAvailable(item) && (!item.category || activeCategoryIds.has(item.category)))
      .sort((a, b) => {
        const orderA = a.order ?? Infinity;
        const orderB = b.order ?? Infinity;
        return orderA - orderB;
      });
  }, [items, activeCategoryIds]);

  const inventoryItems = useMemo(() => {
    return items.filter((item) => item.type === PRODUCT_TYPE.INVENTORY);
  }, [items]);

  const availableCategories = useMemo(() => {
    return categories
      .filter((cat) => activeCategoryIds.has(cat.id))
      .map((cat) => ({
        ...cat,
        count: availableItems.filter((i) => i.category === cat.id).length,
      }))
      .filter((cat) => cat.count > 0)
      .sort((a, b) => {
        const orderA = a.order ?? Infinity;
        const orderB = b.order ?? Infinity;
        return orderA - orderB;
      });
  }, [categories, activeCategoryIds, availableItems]);

  const addCategory = async (category) => {
    setCategories((prev) => [...prev, category]);

    try {
      await ProductHelper.saveCategories([...categories, category]);
    } catch (error) {
      console.error("Failed to save new category", error);
    }
  };

  const updateCategory = async (updatedCategory) => {
    setCategories((prev) => {
      const newCategories = prev.map((c) => (c.id === updatedCategory.id ? { ...c, ...updatedCategory } : c));
      try {
        ProductHelper.saveCategories(newCategories);
      } catch (e) {
        console.error("Failed to persist categories to DB", e);
      }
      return newCategories;
    });
  };

  const addItem = async (item) => {
    setItems((prev) => [...prev, item]);

    setCategories((prev) =>
      prev.map((cat) => (cat.id === item.category ? { ...cat, count: (cat.count || 0) + 1 } : cat)),
    );

    try {
      await ProductHelper.saveItems([...items, item]);
      await machineService.updateProductsToOnlineMachines(item);
    } catch (error) {
      console.error("Failed to save item to DB", error);
    }
  };

  const getInitialData = fetchProducts;

  useEffect(() => {
    if (availableCategories.length > 0) {
      const isSelectedValid = availableCategories.find((c) => c.id === selectedCategory?.id);
      if (!selectedCategory || !isSelectedValid) {
        setSelectedCategory(availableCategories[0]);
      }
    } else {
      setSelectedCategory(null);
    }
  }, [availableCategories, selectedCategory]);

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    const handleProductUpdated = (payload) => {
      if (!payload) return;

      const itemsList = Array.isArray(payload) ? payload : [payload];
      const updateMap = new Map(itemsList.map((item) => [item.id, item]));

      setItems((prev) => prev.map((item) => updateMap.get(item.id) || item));
    };

    const unsub = eventsEmitter.on(EVENTS.PRODUCT_UPDATED, handleProductUpdated);
    return unsub;
  }, []);

  return (
    <ProductContext.Provider
      value={{
        categories,
        availableCategories,
        selectedCategory,
        setSelectedCategory,
        addCategory,
        updateCategory,
        getInitialData,
        items,
        availableItems,
        setItems,
        addItem,
        isLoading,
        refreshProducts: fetchProducts,
        updateProduct,
        priceCategories,
        priceCategoryItems,
        addonMappings,
        chargeGroups,
        charges,
        inventoryItems,
      }}
    >
      {props.children}
    </ProductContext.Provider>
  );
}

export default ProductContext;
